/**
 * Auth Swap Store
 *
 * Tracks whether the app is in the middle of a Firebase Auth identity swap
 * (signInWithCustomToken — the multi-venue picker's "enter venue" / "switch
 * venue" actions). While true, listener error handlers and console
 * interceptors should swallow the transient permission-denied errors that
 * fire as Firestore listeners briefly run with the OLD auth scope before
 * React tears them down and re-subscribes with the NEW scope.
 *
 * Usage at swap sites (web + mobile):
 *   markSwapStart();
 *   await signInWithCustomToken(auth, token);
 *   // Listeners catch up over the next ~1s. Auto-cleared after 1500ms.
 *
 * NOT persisted — only meaningful for ~1s at a time.
 */

import { create } from "zustand";

interface AuthSwapState {
  isSwapping: boolean;
  /** Auto-cleared after 1500ms; safe to call multiple times in a row. */
  markSwapStart: () => void;
  markSwapEnd: () => void;
}

// 8s grace covers slow listener teardown/resubscribe — empirically the
// flood from leadService / customPages / teamProfilesStore can take 2-3s
// after signInWithCustomToken resolves, but some listeners (e.g. paginated
// queries with offline cache miss) re-fire up to 10s later. 8s catches
// the long tail without hiding actual rule violations during routine
// browsing.
const SWAP_GRACE_MS = 8000;
let pendingClear: ReturnType<typeof setTimeout> | null = null;

export const useAuthSwapStore = create<AuthSwapState>((set) => ({
  isSwapping: false,
  markSwapStart: () => {
    if (pendingClear) clearTimeout(pendingClear);
    set({ isSwapping: true });
    pendingClear = setTimeout(() => {
      set({ isSwapping: false });
      pendingClear = null;
    }, SWAP_GRACE_MS);
  },
  markSwapEnd: () => {
    if (pendingClear) clearTimeout(pendingClear);
    pendingClear = null;
    set({ isSwapping: false });
  },
}));

/** Non-hook helper for use inside listener error callbacks. */
export function isSwapNoise(err: unknown): boolean {
  if (!useAuthSwapStore.getState().isSwapping) return false;
  const code = (err as { code?: string } | undefined)?.code;
  return code === "permission-denied";
}

/**
 * Detect Firestore SDK self-logs that should be filtered from console.error:
 *
 *   1. "Uncaught Error in snapshot listener: ... permission-denied"
 *      — fires for ~1s during a venue swap as listeners catch up with the
 *      new auth identity. Only filtered while `isSwapping` is true.
 *
 *   2. "FIRESTORE INTERNAL ASSERTION FAILED" (IDs ca9, b815, etc.)
 *      — Firestore SDK target-state corruption that happens around auth
 *      identity changes. The UI keeps rendering correctly (data still
 *      fetches via re-subscribed listeners), but the SDK retries the bad
 *      operation in a loop and spams the console. Always filtered —
 *      surfacing these to the dev overlay or Sentry doesn't help debug
 *      anything, the issue is internal to the SDK.
 *
 * Used by the console interceptor installed in firebase.ts on web and
 * App.tsx on mobile.
 */
export function isFirestorePermissionDeniedLog(args: unknown[]): boolean {
  // Pass 1: always filter SDK-internal assertion noise. Not user-actionable
  // — the SDK retries internally and surfacing this to dev overlay / Sentry
  // is just spam.
  for (const a of args) {
    if (typeof a === "string" && a.includes("INTERNAL ASSERTION FAILED")) {
      return true;
    }
    if (a instanceof Error && a.message?.includes("INTERNAL ASSERTION FAILED")) {
      return true;
    }
  }

  // Pass 2: filter the Firestore SDK's "Uncaught Error in snapshot listener:
  // ... permission-denied" auto-log. This fires whenever a listener briefly
  // runs with the wrong auth scope (e.g. during a venue swap, when a
  // multi-manager is on the picker page with no active venue, or after sign-
  // out before listeners tear down). Each listener's own onError callback
  // already handles the real error path (service-layer error handlers in
  // stores like teamProfilesStore); the SDK's auto-emit-to-console is
  // duplicative dev-overlay noise.
  //
  // The SDK splits the message across multiple console.error args, so check
  // for "snapshot listener" and "permission-denied" across ALL args, not
  // within a single one.
  let sawSnapshotListener = false;
  let sawPermissionDenied = false;
  for (const a of args) {
    if (typeof a === "string") {
      if (a.includes("snapshot listener")) sawSnapshotListener = true;
      if (a.includes("permission-denied")) sawPermissionDenied = true;
    }
    if (a && typeof a === "object" && "code" in a && (a as { code?: string }).code === "permission-denied") {
      sawPermissionDenied = true;
    }
    if (a instanceof Error && a.message?.includes("permission-denied")) {
      sawPermissionDenied = true;
    }
  }
  if (sawSnapshotListener && sawPermissionDenied) return true;

  return false;
}
